import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconDeviceLaptop,
  IconFingerprint,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { physicianPasskeyService } from "../../services/physicianPasskey.service";
import { physicianPortalService } from "../../services/physicianPortal.service";

export function PhysicianPasskeyManager() {
  const qc = useQueryClient();
  const [deviceName, setDeviceName] = useState("");
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const { data: passkeys = [], isLoading } = useQuery({
    queryKey: ["portalPasskeys"],
    queryFn: () => physicianPortalService.listPasskeys(),
  });

  const removeMutation = useMutation({
    mutationFn: (passkeyId: string) =>
      physicianPortalService.removePasskey(passkeyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portalPasskeys"] });
      notifications.show({ color: "green", message: "Passkey removed" });
    },
    onError: (e: any) =>
      notifications.show({
        color: "red",
        message: e.message || "Failed to remove passkey",
      }),
  });

  const handleAddPasskey = async () => {
    setAddLoading(true);
    setAddError(null);
    try {
      await physicianPasskeyService.addNewPasskey(deviceName || "New Device");
      qc.invalidateQueries({ queryKey: ["portalPasskeys"] });
      notifications.show({
        color: "green",
        message: "Passkey added successfully",
      });
      closeAdd();
      setDeviceName("");
    } catch (e: any) {
      setAddError(e.message || "Failed to add passkey");
    } finally {
      setAddLoading(false);
    }
  };

  if (isLoading)
    return (
      <Center h={100}>
        <Loader size="sm" />
      </Center>
    );

  return (
    <Stack gap="sm">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <IconFingerprint size={18} color="#04119A" />
          <Text
            fw={600}
            style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 15,
              color: "#1e1e3f",
            }}
          >
            Registered Passkeys
          </Text>
        </Group>
        <Button
          size="xs"
          variant="light"
          color="gcpBlue"
          leftSection={<IconPlus size={14} />}
          onClick={openAdd}
        >
          Add Device
        </Button>
      </Group>

      {passkeys.length === 0 ? (
        <Text
          size="sm"
          c="dimmed"
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          No passkeys registered. Add one to enable biometric login.
        </Text>
      ) : (
        <Stack gap={0}>
          {passkeys.map((pk: any) => (
            <Group
              key={pk.id}
              justify="space-between"
              align="center"
              py="sm"
              px="md"
              style={{
                borderBottom: "1px solid #f3f4f6",
                background: "#fafbff",
                borderRadius: 8,
                marginBottom: 6,
              }}
            >
              <Group gap="sm">
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "rgba(4,17,154,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconFingerprint size={18} color="#04119A" />
                </Box>
                <Stack gap={2}>
                  <Text
                    size="sm"
                    fw={500}
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      color: "#1e1e3f",
                    }}
                  >
                    {pk.deviceName || "Unnamed passkey"}
                  </Text>
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                  >
                    Added {new Date(pk.registeredAt).toLocaleDateString()}
                    {pk.lastUsedAt
                      ? ` · Used ${new Date(
                          pk.lastUsedAt
                        ).toLocaleDateString()}`
                      : ""}
                  </Text>
                </Stack>
              </Group>
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                loading={removeMutation.isPending}
                onClick={() => removeMutation.mutate(pk.id)}
                aria-label="Remove passkey"
              >
                <IconTrash size={15} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      )}

      <Modal.Root
        opened={addOpened}
        onClose={closeAdd}
        size="sm"
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: "1.3rem",
                fontWeight: 400,
              }}
            >
              Add New Passkey
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            <form
              id="add-passkey-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddPasskey();
              }}
            >
              <Stack gap="md" py="sm">
                <TextInput
                  label="Device name"
                  description="e.g. MacBook Pro, iPhone, YubiKey"
                  placeholder="My Device"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  leftSection={<IconDeviceLaptop size={16} />}
                  styles={{
                    label: { fontFamily: "'IBM Plex Sans', sans-serif" },
                    description: { fontFamily: "'IBM Plex Sans', sans-serif" },
                  }}
                />
                {addError && (
                  <Alert
                    color="red"
                    icon={<IconAlertCircle size={16} />}
                    radius="md"
                  >
                    {addError}
                  </Alert>
                )}
              </Stack>
            </form>
          </Modal.Body>

          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={closeAdd} disabled={addLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="add-passkey-form"
              loading={addLoading}
              leftSection={<IconFingerprint size={16} />}
              style={{
                background: "linear-gradient(135deg, #04119A 0%, #1a2cc4 100%)",
                border: "none",
              }}
            >
              Register Passkey
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </Stack>
  );
}
